import React, { useEffect } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

const ensureCss = (doc) => {
  try {
    if (doc.__ghsInjected) return;
    const s = doc.createElement("style");
    s.textContent = `
      mark.__focus-mark {
        background: #ffed7a;
        padding: 0 .15em;
        border-radius: 3px;
        box-shadow: 0 0 0 2px rgba(255,191,0,.25);
        animation: __focusPulse .9s ease-out 1;
      }
      @keyframes __focusPulse {
        0%   { background: #ffe04a; box-shadow: 0 0 0 3px rgba(255,191,0,.45); }
        100% { background: #ffed7a; box-shadow: 0 0 0 2px rgba(255,191,0,.25); }
      }
    `;
    doc.head && doc.head.appendChild(s);
    doc.__ghsInjected = true;
  } catch {}
};

const isVisible = (el, doc) => {
  if (!el) return false;
  const win = doc.defaultView || window;
  const cs = win.getComputedStyle(el);
  const r = el.getBoundingClientRect();
  return (
    r.width > 0 &&
    r.height > 0 &&
    cs.display !== "none" &&
    cs.visibility !== "hidden"
  );
};

const norm = (s) => String(s || "").toLowerCase().trim();

const getAllDocs = () => {
  const docs = [document];
  for (const f of document.querySelectorAll("iframe, frame")) {
    try {
      const d = f.contentDocument || f.contentWindow?.document;
      if (d && d.body) docs.push(d);
    } catch {
      /* cross-origin */
    }
  }
  return docs;
};

const isInsideOverlay = (el) => {
  let n = el;
  while (n && n !== n.ownerDocument.body) {
    if (!n.getAttribute) {
      n = n.parentElement;
      continue;
    }
    const role = n.getAttribute("role");
    const aria = n.getAttribute("aria-label");
    if (role === "listbox") return true;
    if (aria && aria.toLowerCase() === "search results") return true;
    n = n.parentElement;
  }
  return false;
};

const findListingRoot = (doc) => {
  const tables = Array.from(doc.querySelectorAll("table"));
  let best = null,
    bestRows = 0;
  for (const t of tables) {
    const thText = Array.from(t.querySelectorAll("th")).map((th) =>
      (th.textContent || "").trim().toLowerCase()
    );
    const hasName = thText.includes("name");
    const hasSize =
      thText.includes("size (bytes)") || thText.includes("size");
    const hasMod =
      thText.includes("last modified") || thText.includes("modified");
    if (hasName && (hasSize || hasMod)) {
      const rows = t.querySelectorAll("tr").length;
      if (rows > bestRows) {
        best = t;
        bestRows = rows;
      }
    }
  }
  return best;
};

const findAnchorByTextWithin = (root, filename) => {
  const needle = norm(filename);
  const anchors = root.querySelectorAll("a");
  for (const a of anchors) {
    if (isInsideOverlay(a)) continue;
    const t = norm(a.textContent);
    if (t && (t === needle || t.includes(needle)) && isVisible(a, root.ownerDocument))
      return a;
  }
  return null;
};

const findElementByTextWithin = (root, filename) => {
  const doc = root.ownerDocument;
  const needle = norm(filename);
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const t = node.nodeValue && node.nodeValue.trim();
      if (!t) return NodeFilter.FILTER_SKIP;
      const tt = norm(t);
      return tt === needle || tt.includes(needle)
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_SKIP;
    },
  });
  let node;
  while ((node = walker.nextNode())) {
    const el = node.parentElement;
    if (!el) continue;
    if (isInsideOverlay(el)) continue;
    if (isVisible(el, doc)) return el;
  }
  return null;
};

const findAnchorByText = (doc, filename) =>
  findAnchorByTextWithin(doc.body, filename);
const findElementByText = (doc, filename) =>
  findElementByTextWithin(doc.body, filename);

const wrapFirstMatchInMark = (doc, el, filename) => {
  const needleLower = filename.toLowerCase();

  // ✅ clear ALL old highlights in this document
  for (const m of doc.querySelectorAll("mark.__focus-mark")) {
    const txt = doc.createTextNode(m.textContent || "");
    m.replaceWith(txt);
  }

  const stack = [el];
  while (stack.length) {
    const n = stack.pop();
    for (let i = n.childNodes.length - 1; i >= 0; i--)
      stack.push(n.childNodes[i]);
    if (n.nodeType === Node.TEXT_NODE) {
      const raw = n.nodeValue || "";
      const idx = raw.toLowerCase().indexOf(needleLower);
      if (idx >= 0) {
        const before = raw.slice(0, idx);
        const match = raw.slice(idx, idx + filename.length);
        const after = raw.slice(idx + filename.length);
        const mark = doc.createElement("mark");
        mark.className = "__focus-mark";
        mark.textContent = match;
        const frag = doc.createDocumentFragment();
        if (before) frag.appendChild(doc.createTextNode(before));
        frag.appendChild(mark);
        if (after) frag.appendChild(doc.createTextNode(after));
        n.replaceWith(frag);

        // ✅ cleanup after 3 seconds
        setTimeout(() => {
          if (mark.isConnected) {
            const txt = doc.createTextNode(mark.textContent || "");
            mark.replaceWith(txt);
          }
        }, 3000);

        return mark;
      }
    }
  }
  el.classList.add("__focus-mark");
  setTimeout(() => el.classList.remove("__focus-mark"), 3000);
  return el;
};

const scrollIntoViewSmart = (doc, el) => {
  const win = doc.defaultView || window;
  let scroller = el.parentElement;
  while (scroller && scroller !== doc.body) {
    const cs = win.getComputedStyle(scroller);
    if (
      (cs.overflowY === "auto" || cs.overflowY === "scroll") &&
      scroller.scrollHeight > scroller.clientHeight
    )
      break;
    scroller = scroller.parentElement;
  }
  if (!scroller || scroller === doc.body) {
    el.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
  } else {
    const rEl = el.getBoundingClientRect();
    const rSc = scroller.getBoundingClientRect();
    const target =
      scroller.scrollTop +
      (rEl.top - rSc.top) -
      (scroller.clientHeight / 2 - rEl.height / 2);
    scroller.scrollTo({ top: Math.max(0, target), behavior: "smooth" });
  }
};

const clearQueryAndHashKeepPathname = () => {
  try {
    const u = new URL(window.location.href);
    u.search = "";
    u.hash = "";
    window.history.replaceState({}, "", u);
  } catch {}
};

export default function GlobalRouteHighlighter() {
  const loc = useLocation();
  const [sp] = useSearchParams();

  useEffect(() => {
    const filename = sp.get("select") || sp.get("file") || "";
    if (!filename) return;

    let tries = 0;
    const maxTries = 100;
    const timer = setInterval(() => {
      tries++;

      for (const doc of getAllDocs()) {
        ensureCss(doc);

        const root = findListingRoot(doc) || doc.body;

        let hostEl =
          findAnchorByTextWithin(root, filename) ||
          findElementByTextWithin(root, filename);

        if (!hostEl) {
          hostEl =
            findAnchorByText(doc, filename) ||
            findElementByText(doc, filename);
        }

        if (hostEl && !isInsideOverlay(hostEl) && isVisible(hostEl, doc)) {
          const markEl = wrapFirstMatchInMark(doc, hostEl, filename);
          scrollIntoViewSmart(doc, markEl);
          markEl.setAttribute?.("tabindex", "0");
          markEl.focus?.({ preventScroll: true }); // focus only, no blue selection

          clearQueryAndHashKeepPathname();
          clearInterval(timer);
          return;
        }
      }

      if (tries >= maxTries) {
        clearInterval(timer);
        clearQueryAndHashKeepPathname();
      }
    }, 100);

    return () => clearInterval(timer);
  }, [loc.key, loc.pathname, loc.search]);

  return null;
}
